import './input.scss';
import React, {
	useEffect,
	useState,
} from 'react';
import {
	FormField, getFormFieldHtmlForValue,
	InputProps,
} from '..';


// Polen: +48 123 456 789
// Norway: +47 12 34 56 78 | 123 45 678
// Sweden: +46 123-4567-890
/**
 * Regular expressions for input validation
 */
export enum Pattern {
  Telephone = '((\\+|00)[\\d]{1,3}[ ]?)(([\\d]{2,3} [\\d]{2,4} [\\d]{2,3})|([\\d]{2,3} [\\d]{2,4} [\\d]{2,3} [\\d]{2,3})|[\\d]{8,13})',
  Email = '[\\a-zA-Z0-9\\-_!#$%&\'*+-/=?^`{|]{1,64}@[\\a-zA-Z0-9\\-_]{1,64}.[a-zA-Z]{1,5}',
  Number = '[\\d]{1,}',
  Text = '[a-zA-ZøæåØÆÅ ]{1,}',
  TextAndNumber = '[a-zA-ZøæåØÆÅ 0-9]{1,}',
}

/*
const getValidFormats = (pattern: Pattern): JSX.Element | undefined => {
	if (pattern === Pattern.Telephone) {
		return (
			<div>
        Gyldige formater:
				<ul>
					<li>0047 12 34 56 78</li>
					<li>+47 12 34 56 78</li>
					<li>+47 12345678</li>
					<li>+48 123 456 789</li>
					<li>+46 123 4567 890</li>
				</ul>
			</div>
		);
	}
	return undefined;
};
*/

export const Input = <T extends string | number | undefined = string>({
	placeholderText,
	usePlaceholderAsLabel = false,
	onClear = () => {},
	type = 'text',
	onChange = () => {},
	rowValue,
	readOnly = false,
	maxLength,
	minLength,
	max,
	min,
	title,
	pattern,
	onValidation,
	hint,
	isPrivate = false,
}: InputProps<T>): JSX.Element => {
	const [isValid, setIsValid] = useState(false);
	const [value, setValue] = useState<T | undefined>(rowValue);
	const [error, setError] = useState<string>();
	const [isBlurTriggered, setIsBlurTriggered] = useState<boolean>(false);

	const setOnValidation = React.useCallback(
		(valid: boolean = isValid) => {
			if (onValidation) {
				onValidation(valid);
			}
		},
		[onValidation, isValid]
	);

	const setErrorMessage = (hasNoErrors: boolean, newValue: T, message: string) => {
		// Checking for null and undefined, as we might not want ot to trigger for 0.
		if (!hasNoErrors && newValue !== undefined && newValue !== null) {
			setError(message);
		}
		else {
			setError(undefined);
		}
	};

	const onChangeDoValidate = (newValue: T) => {
		if (pattern || min || max || minLength || maxLength) {
			let isLengthValid = true;
			const valid = pattern ? new RegExp(pattern, 'gi').exec(`${newValue}`) : true;
			setError(undefined);

			if (type === 'number') {
				if (min && isLengthValid) {
					isLengthValid = newValue && +newValue >= +min;
					setErrorMessage(isLengthValid, newValue, `Minste tillatte verdi er ${min}`);
				}
				if (max && isLengthValid) {
					isLengthValid = newValue && +newValue <= +max;
					setErrorMessage(isLengthValid, newValue, `Høyeste tillatte verdi er ${max}`);
				}
			}
			if (minLength && isLengthValid) {
				isLengthValid = newValue && `${newValue}`.length >= +minLength;
				setErrorMessage(isLengthValid, newValue, `Teksten må minst ha ${minLength} tegn`);
			}
			if (maxLength && isLengthValid) {
				isLengthValid = newValue && `${newValue}`.length <= +maxLength;
				setErrorMessage(isLengthValid, newValue, `Teksten kan maks ha ${maxLength} tegn`);
			}

			if (isLengthValid && !valid) {
				if (type === 'tel' || pattern === Pattern.Telephone) {
					const lackPrefix = !(/^(\+|00)[\d]{1,3}/gi.exec(`${newValue}`));
					setErrorMessage(false, newValue, lackPrefix ? 'Husk landskode' : 'Telefonnummeret er ugyldig');
				}
				else {
					setErrorMessage(false, newValue, 'Feltet er ugyldig');
				}
			}

			const updatedIsValid = !!valid && isLengthValid;
			setIsValid(updatedIsValid);
			setOnValidation(updatedIsValid);
		}
		else {
			setIsValid(true);
		}
		setValue(newValue);
		onChange(newValue);
	};

	useEffect(() => {
		setOnValidation(isValid);
	}, [setOnValidation, isValid]);

	useEffect(() => {
		setValue(rowValue);
	}, [rowValue]);

	useEffect(() => {
		onChangeDoValidate(rowValue);
	});

	return (
		<FormField
			placeholderText={placeholderText}
			usePlaceholderAsLabel={usePlaceholderAsLabel}
			value={rowValue}
			setValue={setValue}
			hint={hint}
			onClear={onClear}
			error={error}
			isBlurTriggered={isBlurTriggered}
			htmlFor={getFormFieldHtmlForValue(placeholderText)}
		>
			<input
				id={getFormFieldHtmlForValue(placeholderText)}
				name={getFormFieldHtmlForValue(placeholderText)}
				type={type}
				value={value}
				onChange={event => onChangeDoValidate(event.target.value as T)}
				placeholder={placeholderText}
				readOnly={readOnly}
				maxLength={maxLength}
				minLength={minLength}
				min={min}
				max={max}
				title={title}
				pattern={pattern}
				onBlur={() => setIsBlurTriggered(true)}
				data-private={isPrivate}
			/>
		</FormField>
	);
};